import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { ProjectInquiryButton, LuxSolutionsLogo, LuxSolutionsMark } from "@/components/ProjectInquiry";
import { Lightbulb, ArrowRight, Sparkles, Gauge, MessageSquareText, Layers, Ruler, BookOpenCheck } from "lucide-react";

const HERO = "https://static.prod-images.emergentagent.com/jobs/ce1cbc9d-1dad-4cd9-98ed-a62f3c8b6634/images/acfc7e465de1d4c640761cfcec30838d893a3b2758305121af80d740a1159ee2.png";

const features = [
  { icon: Layers, title: "10 fundierte Module", text: "Vom LUX-Denkmodell über Photometrie, Normen und Leuchten bis zum Abschlussprojekt." },
  { icon: Gauge, title: "Sichtbarer Fortschritt", text: "Fortschrittsringe pro Modul und Gesamtkurs zeigen Ihnen jederzeit, wo Sie stehen." },
  { icon: Sparkles, title: "Interaktive Erklärungen", text: "Farbtemperatur, Abstandsgesetz, Abstrahlwinkel & Lichthierarchie zum Anfassen." },
  { icon: BookOpenCheck, title: "Quiz zum Vertiefen", text: "Nach jedem Modul festigen Sie das Wissen mit Quizfragen und Erklärungen." },
  { icon: MessageSquareText, title: "KI-Licht-Coach", text: "Stellen Sie jederzeit Fachfragen und lassen Sie Ihr Lichtkonzept kritisch prüfen." },
  { icon: Ruler, title: "Praxis-Werkzeuge", text: "Lichtbriefing-Generator und Normen-Ampel für echte Projekte." },
];

export default function Landing() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (ready && user) navigate("/dashboard");
  }, [ready, user, navigate]);

  return (
    <div className="min-h-screen bg-background grain">
      {/* Nav */}
      <header className="relative z-20 max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="relative">
            <Lightbulb className="h-7 w-7 text-gold" />
            <div className="absolute inset-0 blur-md bg-gold/40 rounded-full -z-10 animate-pulse-glow" />
          </div>
          <div className="leading-none">
            <span className="font-heading font-extrabold text-xl">LUX</span>
            <span className="block text-[9px] uppercase tracking-[0.25em] text-muted-foreground">Lighting Academy</span>
          </div>
          <span className="hidden sm:flex items-center gap-2 ml-3 pl-3 border-l hairline">
            <span className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground">by</span>
            <LuxSolutionsLogo imgClassName="h-10 w-auto" />
          </span>
          <span className="flex sm:hidden items-center ml-2.5 pl-2.5 border-l hairline">
            <LuxSolutionsMark imgClassName="h-7 w-auto rounded-md" />
          </span>
        </div>
        <Link to="/login" data-testid="nav-login-link" className="px-5 py-2.5 rounded-full glass text-sm hover:glow-gold transition-all duration-300">
          Anmelden
        </Link>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={HERO} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/70 to-background" />
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 sm:pt-24 pb-24">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
            <div className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full glass text-sm sm:text-base text-gold mb-6 font-medium">
              <Sparkles className="h-4 w-4 sm:h-5 sm:w-5" /> Lichtplanungskurs für Architekten
            </div>
            <h1 className="font-heading font-extrabold text-4xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
              Licht ist das Material,<br />
              mit dem Architektur<br />
              <span className="text-gold glow-text">sichtbar</span> wird.
            </h1>
            <p className="text-lg text-foreground/70 mt-6 max-w-xl leading-relaxed">
              Ein interaktiver, fachlich fundierter Kurs, der Ihnen ein klares System gibt, um Licht früh, kreativ und souverän in Ihre Projekte zu integrieren – mit Grafiken, animierten Erklärungen, Quiz und KI-Coach.
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-9">
              <Link to="/login?register=1" data-testid="hero-cta" className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gold text-[#0a0d12] font-semibold hover:shadow-[0_8px_40px_-8px_rgba(212,175,55,0.7)] transition-all duration-300">
                Erstes Modul gratis testen
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <div className="flex items-center gap-6 text-sm text-muted-foreground">
                <div><span className="font-heading font-bold text-foreground text-lg">10</span> Module</div>
                <div><span className="font-heading font-bold text-foreground text-lg">30+</span> Lektionen</div>
                <div><span className="font-heading font-bold text-foreground text-lg">KI</span> Coach</div>
              </div>
            </div>
            <p className="text-sm text-foreground/60 mt-4 flex items-center gap-2" data-testid="hero-free-hint">
              <Sparkles className="h-3.5 w-3.5 text-gold" /> Das erste Modul des Kurses gratis testen – ohne Lizenzschlüssel.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="rounded-2xl glass p-6 hover:glow-gold transition-all duration-500 group"
              >
                <div className="h-11 w-11 rounded-xl bg-gold/15 border border-gold/25 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Icon className="h-5 w-5 text-gold" />
                </div>
                <h3 className="font-heading font-bold text-lg">{f.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.text}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* CTA strip */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <div className="rounded-3xl glass-strong radial-fade p-10 sm:p-14 text-center">
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl">Werde zur Lichtinstanz im Projekt.</h2>
          <p className="text-foreground/70 mt-4 max-w-xl mx-auto">Fundiertes Wissen aus Lichttechnik, Normen und Gestaltung – verständlich aufbereitet für den Entwurfsalltag.</p>
          <div className="flex flex-wrap items-center justify-center gap-4 mt-8">
            <Link to="/login?register=1" data-testid="cta-bottom" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gold text-[#0a0d12] font-semibold hover:shadow-[0_8px_40px_-8px_rgba(212,175,55,0.7)] transition-all duration-300">
              Erstes Modul gratis testen <ArrowRight className="h-4 w-4" />
            </Link>
            <ProjectInquiryButton
              variant="ghost"
              source="landing"
              testid="project-inquiry-landing-button"
              label="Kostenlose Erstberatung anfragen"
              subject="Anfrage kostenlose Erstberatung – Lichtplanung (LUX Academy)"
              body={"Guten Tag\n\nIch interessiere mich für eine Lichtplanung / ein Projekt und möchte eine kostenlose Erstberatung bei Lux Solutions GmbH anfragen.\n\nKurz zu meinem Vorhaben:\n- Projekt/Raum: \n- Nutzung: \n- Zeitrahmen: \n\nFreundliche Grüsse"}
              className="px-7 py-3.5"
            />
          </div>
        </div>
      </section>

      <footer className="border-t hairline py-10 flex flex-col items-center gap-4 text-center">
        <LuxSolutionsLogo />
        <p className="text-xs text-muted-foreground">
          LUX Architect Lighting Academy · Ein Angebot der Lux Solutions GmbH · Lichtplanung & Lichtdesign für Architekt:innen
        </p>
      </footer>
    </div>
  );
}
